<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { useVModel } from "@vueuse/core"
import { cn } from "@/lib/utils"

const props = defineProps<{
  class?: HTMLAttributes["class"]
  defaultValue?: string | number
  modelValue?: string | number
}>()

const emits = defineEmits<{
  (e: "update:modelValue", payload: string | number): void
}>()

const modelValue = useVModel(props, "modelValue", emits, {
  passive: true,
  defaultValue: props.defaultValue,
})

const textareaRef = ref<HTMLTextAreaElement | null>(null)

const resize = () => {
  const el = textareaRef.value
  if (!el) return
  el.style.height = "auto"
  const scrollHeight = el.scrollHeight
  const maxHeight = Number.parseFloat(getComputedStyle(el).maxHeight || "")
  if (!Number.isNaN(maxHeight) && maxHeight > 0) {
    const nextHeight = Math.min(scrollHeight, maxHeight)
    el.style.height = `${nextHeight}px`
    el.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden"
  } else {
    el.style.height = `${scrollHeight}px`
    el.style.overflowY = "hidden"
  }
}

onMounted(() => {
  nextTick(resize)
})

watch(modelValue, () => {
  nextTick(resize)
})
</script>

<template>
  <textarea
    ref="textareaRef"
    v-model="modelValue"
    :class="cn('box-border flex min-h-[60px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:bg-background', props.class)"
  />
</template>
